<template>
  <div class="q-page">
    <div class="container py-4">

      <!-- Empty cart state -->
      <div v-if="basketCount === 0" class="q-empty-cart">
        <i class="bi bi-cart-x q-empty-cart__icon"></i>
        <p class="q-empty-cart__title">Tu carrito está vacío</p>
        <p class="q-empty-cart__sub">Agregá productos antes de continuar con la compra.</p>
        <router-link :to="{ name: 'Store', params: { company: $route.params.company } }" class="btn btn-primary mt-2">
          <i class="bi bi-shop-window me-2"></i>Volver a la tienda
        </router-link>
      </div>

      <template v-if="basketCount > 0">

      <!-- Header -->
      <p class="q-section-title mb-4">Datos del pedido</p>

      <!-- Step indicator -->
      <div class="q-steps mb-4">
        <div v-for="n in 4" :key="n" class="q-step" :class="{ 'q-step--active': step === n, 'q-step--done': step > n }">
          <span class="q-step__num">{{ n }}</span>
          <span class="q-step__label">{{ stepLabels[n-1] }}</span>
        </div>
      </div>

      <div class="row g-4">

        <!-- Form column -->
        <div class="col-lg-7">
          <form @submit.prevent="nextStep">
            <div class="q-checkout-card">

              <!-- Step 1: Contacto -->
              <div v-if="step === 1">
                <h2 class="q-checkout-card__title">Datos de contacto</h2>
                <div class="row g-3">
                  <div class="col-12">
                    <label class="q-form-label">Email *</label>
                    <input v-model="formValues.client.email" type="email" class="form-control" required />
                  </div>
                  <div class="col-12">
                    <label class="q-form-label">Nombre completo *</label>
                    <input v-model="formValues.client.name" type="text" class="form-control" required />
                  </div>
                  <div class="col-sm-6">
                    <label class="q-form-label">Teléfono *</label>
                    <input v-model="formValues.client.phone" type="tel" class="form-control" required />
                  </div>
                  <div class="col-sm-6">
                    <label class="q-form-label">Teléfono móvil</label>
                    <input v-model="formValues.client.mobphone" type="tel" class="form-control" />
                  </div>
                  <div class="col-12">
                    <label class="q-form-label">Dirección *</label>
                    <input v-model="formValues.client.address" type="text" class="form-control" required />
                  </div>
                  <div class="col-12">
                    <label class="q-form-label">Localidad *</label>
                    <input v-model="formValues.client.city" type="text" class="form-control" required />
                  </div>
                </div>
              </div>

              <!-- Step 2: Envio -->
              <div v-if="step === 2">
                <h2 class="q-checkout-card__title">Datos de envío</h2>

                <div class="q-radio-group mb-4">
                  <div class="form-check form-check-inline">
                    <input class="form-check-input" type="radio" id="envio" :value="false" v-model="formValues.retireOnSite" />
                    <label class="form-check-label" for="envio">Envío a domicilio</label>
                  </div>
                  <div class="form-check form-check-inline">
                    <input class="form-check-input" type="radio" id="retiro" :value="true" v-model="formValues.retireOnSite" />
                    <label class="form-check-label" for="retiro">Retiro en el local</label>
                  </div>
                </div>

                <div class="row g-3" :class="{ 'q-disabled-section': formValues.retireOnSite }">
                  <div class="col-12">
                    <label class="q-form-label">Calle</label>
                    <input v-model="formValues.shipping.address" type="text" class="form-control" :disabled="formValues.retireOnSite" />
                  </div>
                  <div class="col-sm-4">
                    <label class="q-form-label">Número</label>
                    <input v-model="formValues.shipping.postalcode" type="text" class="form-control" :disabled="formValues.retireOnSite" />
                  </div>
                  <div class="col-sm-4">
                    <label class="q-form-label">Depto</label>
                    <input v-model="formValues.shipping.floor" type="text" class="form-control" :disabled="formValues.retireOnSite" />
                  </div>
                  <div class="col-sm-4">
                    <label class="q-form-label">Barrio</label>
                    <input v-model="formValues.shipping.district" type="text" class="form-control" :disabled="formValues.retireOnSite" />
                  </div>
                  <div class="col-12">
                    <label class="q-form-label">Entre calles</label>
                    <input v-model="formValues.shipping.directive" type="text" class="form-control" :disabled="formValues.retireOnSite" />
                  </div>
                </div>

                <hr class="q-divider my-4" />

                <div class="form-check mb-3">
                  <input class="form-check-input" type="checkbox" id="otraPersona" v-model="formValues.retireByThird" :disabled="!formValues.retireOnSite" />
                  <label class="form-check-label" for="otraPersona">Retira otra persona</label>
                </div>

                <div class="row g-3" :class="{ 'q-disabled-section': !formValues.retireByThird }">
                  <div class="col-sm-6">
                    <label class="q-form-label">Nombre del tercero</label>
                    <input v-model="formValues.retire.name" type="text" class="form-control" :disabled="!formValues.retireByThird" />
                  </div>
                  <div class="col-sm-6">
                    <label class="q-form-label">DNI del tercero</label>
                    <input v-model="formValues.retire.dni" type="text" class="form-control" :disabled="!formValues.retireByThird" />
                  </div>
                </div>
              </div>

              <!-- Step 3: Facturacion -->
              <div v-if="step === 3">
                <h2 class="q-checkout-card__title">Datos de facturación</h2>

                <div class="q-radio-group mb-4">
                  <div class="form-check form-check-inline">
                    <input class="form-check-input" type="radio" id="consumidor" :value="true" v-model="formValues.finalConsumer" />
                    <label class="form-check-label" for="consumidor">Soy consumidor final</label>
                  </div>
                  <div class="form-check form-check-inline">
                    <input class="form-check-input" type="radio" id="factura" :value="false" v-model="formValues.finalConsumer" />
                    <label class="form-check-label" for="factura">Necesito factura</label>
                  </div>
                </div>

                <div class="row g-3" :class="{ 'q-disabled-section': formValues.finalConsumer }">
                  <div class="col-12">
                    <label class="q-form-label">Razón social o nombre</label>
                    <input v-model="formValues.billing.name" type="text" class="form-control" :disabled="formValues.finalConsumer" />
                  </div>
                  <div class="col-sm-6">
                    <label class="q-form-label">CUIT / CUIL / DNI</label>
                    <input v-model="formValues.billing.cuit" type="text" class="form-control" :disabled="formValues.finalConsumer" />
                  </div>
                  <div class="col-sm-6">
                    <label class="q-form-label">Tipo de IVA</label>
                    <select v-model="formValues.billing.type" class="form-select" :disabled="formValues.finalConsumer">
                      <option disabled value="">Seleccione una opción</option>
                      <option value="mon">Monotributo</option>
                      <option value="ins">Inscripto</option>
                      <option value="exe">Exento</option>
                    </select>
                  </div>
                </div>
              </div>

              <!-- Step 4: Pago -->
              <div v-if="step === 4">
                <h2 class="q-checkout-card__title">Forma de pago</h2>

                <div v-if="configuration.show_mercadopago" class="q-pay-option mb-3"
                  :class="{ 'q-pay-option--active': selectedOption === 'mercadopago' }"
                  @click="selectOption('mercadopago')">
                  <div class="q-pay-option__label">
                    <div class="form-check m-0">
                      <input class="form-check-input" type="radio" v-model="selectedOption" value="mercadopago" id="mp" />
                      <label class="form-check-label fw-semibold" for="mp">Pagar con MercadoPago</label>
                    </div>
                  </div>
                  <img src="https://http2.mlstatic.com/frontend-assets/ui-navigation/5.14.3/mercadopago/logo__large.png" alt="MercadoPago" class="q-pay-option__logo" />
                </div>

                <div class="q-pay-option mb-4"
                  :class="{ 'q-pay-option--active': selectedOption === 'efectivo' }"
                  @click="selectOption('efectivo')">
                  <div class="q-pay-option__label">
                    <div class="form-check m-0">
                      <input class="form-check-input" type="radio" v-model="selectedOption" value="efectivo" id="efectivo" />
                      <label class="form-check-label fw-semibold" for="efectivo">A convenir</label>
                    </div>
                  </div>
                  <i class="bi bi-cash q-pay-option__icon"></i>
                </div>

                <div>
                  <label class="q-form-label">Notas del pedido</label>
                  <textarea v-model="formValues.notes" class="form-control" rows="3"></textarea>
                </div>
              </div>

            </div>

            <!-- Nav buttons -->
            <div class="d-flex gap-2 mt-3">
              <button type="button" @click="prevStep" class="btn btn-outline-secondary" :disabled="step === 1">
                <i class="bi bi-arrow-left me-1"></i>Atrás
              </button>
              <button v-if="step < 4" type="submit" class="btn btn-primary ms-auto">
                Continuar<i class="bi bi-arrow-right ms-1"></i>
              </button>
              <button v-if="step === 4" type="button" class="btn btn-primary ms-auto" @click="setFormValues">
                <i class="bi bi-check2 me-1"></i>Finalizar pedido
              </button>
            </div>

          </form>
        </div>

        <!-- Order summary column -->
        <div class="col-lg-5">
          <div class="q-checkout-card">
            <h2 class="q-checkout-card__title">Listado de productos</h2>
            <ul class="list-unstyled q-basket-list">
              <li
                v-for="item in basket"
                :key="item.product.id + '-' + item.variant.id"
                class="q-basket-list__item"
              >
                <MiniCartItem :item="item" :withaddbuttons="false" />
              </li>
            </ul>
            <div class="q-checkout-total">
              <span class="q-form-label">Total</span>
              <span class="q-price">${{ basketSum }}</span>
            </div>
          </div>
        </div>

      </div>

      </template><!-- v-if basketCount > 0 -->

    </div>
  </div>
</template>

<script>
import MiniCartItem from "../components/MiniCartItem";
import { mapGetters, mapState, mapActions } from "vuex";

export default {
  name: "Checkout-Flow",
  components: { MiniCartItem },
  data() {
    return {
      selectedOption: "efectivo",
      step: 1,
      stepLabels: ["Contacto", "Envío", "Facturación", "Pago"],
      formValues: {
        client: { email: "", name: "", phone: "", mobphone: "", address: "", city: "", dni: "" },
        retireOnSite: true,
        shipping: { address: "", postalcode: "", floor: "", district: "", directive: "" },
        retireByThird: false,
        retire: { name: "", dni: "" },
        finalConsumer: true,
        billing: { name: "", cuit: "", type: "" },
        paymethod: { description: "efectivo", token: "" },
        notes: ""
      },
    };
  },
  mounted() {
    if (this.user != null) {
      this.formValues.client.email = this.user.email;
      this.formValues.client.phone = this.user.phone !== null ? this.user.phone : "";
    }
    if (this.client != null) {
      this.formValues.client.name = this.client.name;
      if (this.user.phone == null) this.formValues.client.phone = this.client.phone_1;
      this.formValues.client.address = this.client.address;
      this.formValues.client.city = this.client.city;
      this.formValues.client.dni = this.client.dni;
      this.formValues.shipping.address = this.client.address;
      this.formValues.shipping.postalcode = this.client.postalcode;
      this.formValues.billing.name = this.client.business !== null ? this.client.business : this.client.name;
      this.formValues.billing.cuit = this.client.cuit;
      if (this.client.iva_type != "Consumidor Final") {
        this.formValues.finalConsumer = false;
        switch (this.client.iva_type) {
          case "Monotributo": this.formValues.billing.type = "mon"; break;
          case "Excento": this.formValues.billing.type = "exc"; break;
          case "Responsable Inscripto": this.formValues.billing.type = "ins"; break;
        }
      }
    }
  },
  computed: {
    ...mapState("ecommerce", ["basket"]),
    ...mapGetters({
      basketCount: "ecommerce/basketCount",
      basketSum: "ecommerce/basketSum",
      order: "ecommerce/order",
      client: "ecommerce/client",
      user: "ecommerce/user",
      configuration: "ecommerce/configuration",
      mp_url: "ecommerce/mp_url",
    }),
  },
  watch: {
    order(newOrder) {
      if (newOrder !== undefined && newOrder.id !== undefined) {
        this.$router.push({ name: "CompleteOrder", params: { company: this.$route.params.company } });
      } else {
        alert("Error al crear la orden: " + newOrder);
      }
    },
    mp_url(newurl) {
      this.formValues.paymethod.token = newurl.data.external_reference;
    },
  },
  methods: {
    ...mapActions({
      createOrderEcommerce: "ecommerce/createOrderEcommerce",
      getMercadoPagoUrl: "ecommerce/getMercadoPagoUrl",
    }),
    nextStep() {
      if (this.basketCount === 0) {
        this.$router.push({ name: "Home", params: { company: this.$route.params.company } });
        return;
      }
      if (this.configuration.show_mercadopago == true) {
        this.getMercadoPagoUrl({ values: this.formValues, items: this.basket });
      }
      this.step++;
    },
    prevStep() { this.step--; },
    setFormValues() {
      if (this.basketCount === 0) {
        this.$router.push({ name: "Home", params: { company: this.$route.params.company } });
        return;
      }
      this.createOrderEcommerce({ values: this.formValues, items: this.basket, totals: { subtotal: this.basketSum } });
    },
    selectOption(option) {
      this.selectedOption = option;
      this.formValues.paymethod.description = option;
    },
  },
};
</script>

<style scoped>
/* Empty cart */
.q-empty-cart {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
}
.q-empty-cart__icon {
  font-size: 3.5rem;
  color: var(--q-text-subtle);
  margin-bottom: 16px;
}
.q-empty-cart__title {
  font-family: var(--q-font-display);
  font-size: 1.1rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: var(--q-text);
  margin: 0 0 8px;
}
.q-empty-cart__sub {
  font-size: 13px;
  color: var(--q-text-muted);
  margin: 0;
}

.q-steps {
  display: flex;
  gap: 0;
  border-bottom: 1px solid var(--q-border);
  padding-bottom: 16px;
}
.q-step {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--q-text-subtle);
  font-family: var(--q-font-display);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
.q-step__num {
  width: 22px; height: 22px;
  border-radius: 50%;
  background: var(--q-bg-elevated);
  border: 1px solid var(--q-border);
  display: flex; align-items: center; justify-content: center;
  font-size: 11px; font-weight: 700;
  flex-shrink: 0;
}
.q-step--active .q-step__num {
  background: var(--primary-color);
  border-color: var(--primary-color);
  color: #ffffff;
}
.q-step--active { color: var(--primary-color); }
.q-step--done .q-step__num {
  background: var(--q-bg-elevated);
  border-color: var(--primary-color);
  color: var(--primary-color);
}
.q-step--done { color: var(--q-text-muted); }

.q-checkout-card {
  background: var(--q-bg-surface);
  border: 1px solid var(--q-border);
  border-radius: var(--q-radius-lg);
  padding: 28px;
}
.q-checkout-card__title {
  font-family: var(--q-font-display);
  font-size: 1rem; font-weight: 700;
  letter-spacing: 0.04em;
  color: var(--q-text);
  margin-bottom: 24px;
}
.q-form-label {
  font-family: var(--q-font-display);
  font-size: 10px; font-weight: 600;
  letter-spacing: 0.1em; text-transform: uppercase;
  color: var(--q-text-muted); display: block; margin-bottom: 6px;
}

.q-radio-group {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}
.q-radio-group .form-check-label { color: var(--q-text-muted); font-size: 13px; }

.q-disabled-section { opacity: 0.4; pointer-events: none; }

/* Payment options */
.q-pay-option {
  border: 1px solid var(--q-border);
  border-radius: var(--q-radius);
  padding: 14px 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  transition: border-color var(--q-transition), background var(--q-transition);
}
.q-pay-option:hover { border-color: var(--primary-color); }
.q-pay-option--active {
  border-color: var(--primary-color);
  background: rgba(0, 112, 243, 0.08);
}
.q-pay-option__logo { height: 24px; width: auto; filter: brightness(0.85); }
.q-pay-option__icon { font-size: 1.75rem; color: var(--q-text-muted); }
.q-pay-option__label { flex: 1; }

/* Basket list */
.q-basket-list { max-height: 420px; overflow-y: auto; padding-right: 4px; }
.q-basket-list__item { padding: 12px 0; border-bottom: 1px solid var(--q-border); }
.q-basket-list__item:last-child { border-bottom: none; }

.q-checkout-total {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 16px;
  border-top: 1px solid var(--q-border);
  margin-top: 4px;
}
.q-price { font-family: var(--q-font-mono); font-size: 1.25rem; color: var(--primary-color); font-weight: 700; }

/* Bootstrap 5 form-check radio accent */
.form-check-input:checked {
  background-color: var(--primary-color);
  border-color: var(--primary-color);
}
</style>
